import React from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';

import { FeedbackContext, LocalizationContext } from '@cvt/contexts';
import { useRouter } from '@cvt/hooks/useRouter';

import { cacheKeys as authCacheKeys } from '@modules/Auth/config';

import { cacheKeys } from '../config';
import { userClient } from '../client/userClient';
import { UserContext } from '../contexts';

export const useUserCrud = () => {

  const router = useRouter();
  const { user } = React.useContext(UserContext);
  const { dictionary } = React.useContext(LocalizationContext);
  const { triggerFeedback, genericErrorFeedback } = React.useContext(FeedbackContext);
  const queryClient = useQueryClient();

  const createUser = useMutation(userClient.createUser, {
    mutationKey: [cacheKeys.createUser],
    onSuccess: (data) => {
      queryClient.invalidateQueries([cacheKeys.getUsers]);
      router.users.edit(data.data.id).go();
      triggerFeedback({
        severity: 'success',
        message: dictionary.feedback.changesSaved,
      });
    },
    onError: () => {
      genericErrorFeedback();
    },
  });

  const editUser = useMutation(({ id, ...data }: Users.Edit) => userClient.editUser({ id, ...data }), {
    mutationKey: [cacheKeys.editUser],
    onSuccess: async (data) => {
      queryClient.invalidateQueries([cacheKeys.getUsers]);
      queryClient.invalidateQueries([cacheKeys.getUser, {
        id: data.data.id,
      }]);
      if (user?.id === data.data.id) {
        queryClient.setQueryData([authCacheKeys.getMe], data);
        await queryClient.invalidateQueries([authCacheKeys.getMe]);
      }
    },
    onError: () => {
      genericErrorFeedback();
    },
  });

  const deleteUser = useMutation((id: number) => userClient.deleteUser({ id }), {
    mutationKey: [cacheKeys.deleteUser],
    onSuccess: (data, id) => {
      // TODO: verify if id is correct
      queryClient.invalidateQueries([cacheKeys.getUsers]);
      queryClient.removeQueries([cacheKeys.getUser, {
        id,
      }]);
      triggerFeedback({
        severity: 'success',
        message: dictionary.feedback.changesSaved,
      });
    },
    onError: () => {
      genericErrorFeedback();
    },
  });

  const inviteUser = useMutation(userClient.inviteUser, {
    mutationKey: [cacheKeys.inviteUser],
    onSuccess: (result, request) => {
      triggerFeedback({
        severity: 'success',
        message: dictionary.users.invite.dialog.invitationSentInfo(request.email),
      });
    },
    onError: () => {
      genericErrorFeedback();
    },
  });

  return {
    createUser: createUser.mutateAsync,
    editUser: editUser.mutateAsync,
    deleteUser: deleteUser.mutateAsync,
    inviteUser: inviteUser.mutateAsync,
  };
};
